import { Text, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import SurtaxLine from './SurtaxLine';
import { useClientTaxRateConfig, useObligationCountyAllocation } from '../../services/complianceApi';

export interface ObligationSurtaxLineProps {
  clientId: string;
  /**
   * The obligation id this obligation's FILINGS carry — never assume it is the row's own id.
   * On the client-detail card that is `filingObligationId`; on the obligations page it is the
   * Cosmos obligation's id. Null when no single obligation generates filings: the line then shows
   * the client default only, which is what such filings would compute at.
   */
  obligationId: string | null | undefined;
}

/**
 * Fetches what `SurtaxLine` needs for one obligation. Kept separate so the two obligation tables
 * share one loading and failure rule.
 *
 * ⚠ A failed load renders as "could not be loaded", never as one of the four real states. Falling
 * through to `SurtaxLine` with no data would render "county not set — filings cannot compute",
 * which is a statement about the client's configuration that a network error does not support.
 */
export default function ObligationSurtaxLine({ clientId, obligationId }: ObligationSurtaxLineProps) {
  const { t } = useTranslation();
  const cfg = useClientTaxRateConfig(clientId);
  const alloc = useObligationCountyAllocation(clientId, obligationId ?? '');

  if (cfg.isError || (obligationId && alloc.isError)) {
    return (
      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }} data-testid="surtax-line-unavailable">
        {t('surtaxLine.unavailable')}
      </Text>
    );
  }

  // Render nothing until both halves are in, so a split never flashes as the client default.
  if (!cfg.data || (obligationId && !alloc.data)) return null;

  return (
    <SurtaxLine
      allocation={obligationId ? alloc.data?.current : null}
      blendedSurtaxRate={obligationId ? alloc.data?.blendedSurtaxRate : null}
      rateConfig={cfg.data}
    />
  );
}
